'use client'

import React, { useCallback, useMemo, useState } from 'react'

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  Modal,
  ModalAction,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@/components'

import { appSelectors, useAppStore } from '@/store'
import { getColumns, HistoryEvents } from './History.config'
import {
  DataTableCustomEvent,
  DataTableCustomEventFeature,
  DataTableCustomEventParams,
} from '@/components/ui/Table.features'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

export const History: React.FC<{ onlyInspected?: boolean }> = () => {
  const processes = useAppStore(appSelectors.processes)
  const actions = useAppStore(appSelectors.asyncActions)
  const locale = useAppStore(appSelectors.locale)
  const { user } = useAppStore(appSelectors.auth)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const router = useRouter()
  const t = useTranslations('history')

  const handleCustomEvent: DataTableCustomEvent<HistoryEvents, unknown> =
    useCallback(
      async (params: DataTableCustomEventParams<HistoryEvents>) => {
        const { eventParams, eventName } = params
        switch (eventName) {
          case HistoryEvents.DELETE:
            return setDeleteId(eventParams.id)
          case HistoryEvents.DOWNLOAD:
            return await actions.download(eventParams.id, eventParams.format)
          case HistoryEvents.SHOW:
            return router.push(`/${locale}/process/${eventParams?.id}`)
        }
      },
      [actions, locale, router],
    )

  const data = useMemo(
    () =>
      Object.values(processes ?? {}).map((process) => ({
        id: process.id ?? 'N/A',
        url: process.url ?? 'N/A',
        settings: process.settings ?? null,
        pages: process?.pages ?? {
          count: 0,
          items: [],
          limit: 99999,
          offset: 0,
          total: 0,
        },
        isFinished: process.isFinished ?? false,
        status: process.isFinished ? 'finished' : 'in progress',
        pageCount: process?.pageCount ?? 0,
      })),
    [processes],
  )

  const columns = useMemo(() => getColumns(t), [t])

  const table = useReactTable({
    _features: [DataTableCustomEventFeature],
    data,
    columns: [
      columns.id,
      columns.url,
      columns.status,
      columns.pageCount,
      columns.actions,
    ],
    getCoreRowModel: getCoreRowModel(),
    onCustomEvent: handleCustomEvent,
  })

  React.useEffect(() => {
    if (!processes && user) {
      actions.load()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processes, user])

  return (
    <div className="xl:w-[60%] overflow-hidden mx-auto mt-12 text-base text-white">
      <Text variant="heading3Xl" className="text-white my-12">
        {t('title')}
      </Text>
      <div className="rounded-xl border border-2 border-white/10 p-3">
        <Table className="w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="overflow-hidden"
                      style={{ width: cell.column.columnDef.size }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Object.values(columns).length}
                  className="h-24 text-center"
                >
                  {t('noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Modal
        open={!!deleteId}
        setOpen={() => setDeleteId(null)}
        closeable
        title={t('delete.title')}
        actions={{
          [ModalAction.CANCEL]: {
            onClick: () => setDeleteId(null),
            caption: t('delete.actions.cancel'),
          },
          [ModalAction.ACCEPT]: {
            onClick: async () => {
              await actions.deleteProcess(deleteId ?? '')
              setDeleteId(null)
            },
            caption: t('delete.actions.delete'),
          },
        }}
      >
        {t('delete.description')}
      </Modal>
    </div>
  )
}
